import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sportsApi } from '../../services/sportsApi';
import { matchdayPlanningApi } from '../../services/matchdayPlanningApi';
import { Match, Season, Club, MatchStatus, MatchType } from '../../types/sports';
import { MatchdayPlanning } from '../../types/matchdayPlanning';
import { LocalMatch } from '../../types/localMatchday';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferredViewMode } from '../../hooks/usePreferredViewMode';
import { CircleDot } from 'lucide-react';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import UpcomingMatchCard from './UpcomingMatchCard';
import CreateOwnMatchModal from './CreateOwnMatchModal';
import Plantafel from './Plantafel';

interface UpcomingMatchesDashboardProps {
  daysAhead?: number;
  maxMatches?: number;
  showHeader?: boolean;
  clubId?: number;
  onBackToLanding?: () => void;
}

type SortOrder = 'asc' | 'desc';
type ViewMode = 'cards' | 'plantafel' | 'calendar';

const UpcomingMatchesDashboard: React.FC<UpcomingMatchesDashboardProps> = ({
  daysAhead = 30,
  maxMatches = 12,
  showHeader = true,
  clubId,
  onBackToLanding
}) => {
  const { t, i18n } = useTranslation(['common']);
  const [matches, setMatches] = useState<(Match | LocalMatch)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [showPastMatches, setShowPastMatches] = useState(false);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null);
  const [filterClub, setFilterClub] = useState<Club | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<ViewMode>('cards');
  const [totalMatchCount, setTotalMatchCount] = useState(0);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { user: partnerUser } = usePartnerAuth();

  // Easter egg state
  const [easterEggClicks, setEasterEggClicks] = useState(0);
  const [showDriverPoints, setShowDriverPoints] = useState(false);
  const [driverPointsData, setDriverPointsData] = useState<{ name: string; points: number; matches: number }[]>([]);
  const [driverPointsLoading, setDriverPointsLoading] = useState(false);

  // Determine if this is a partner user
  const isPartnerUser = partnerUser !== null;

  console.log('UpcomingMatchesDashboard - User check:', {
    clubUser: !!user,
    partnerUser: !!partnerUser,
    isPartnerUser,
    partnerUserDetails: partnerUser
  });

  useEffect(() => {
    if (!isPartnerUser) {
      loadSeasons();
      loadClubs();
    }
  }, [isPartnerUser]);

  // Load club name when clubId is provided
  useEffect(() => {
    const loadFilterClub = async () => {
      if (clubId) {
        try {
          const club = await sportsApi.getClubById(clubId);
          setFilterClub(club);
        } catch (err) {
          console.error('Error loading filter club:', err);
          setFilterClub(null);
        }
      } else {
        setFilterClub(null);
      }
    };
    loadFilterClub();
  }, [clubId]);

  useEffect(() => {
    console.log('UpcomingMatchesDashboard - loadUpcomingMatches triggered, isPartnerUser:', isPartnerUser);
    loadUpcomingMatches();
  }, [daysAhead, clubId, selectedSeasonId, isPartnerUser]);

  // Auto-expand only the current week when matches are loaded
  useEffect(() => {
    if (matches.length > 0) {
      const groupedMatches = groupMatchesByWeek(matches);
      const today = new Date();

      // Find the week that contains today
      const currentWeekKey = Object.keys(groupedMatches).find(weekStart => {
        const weekStartDate = new Date(weekStart);
        const weekEndDate = new Date(weekStartDate);
        weekEndDate.setDate(weekStartDate.getDate() + 6);

        return today >= weekStartDate && today <= weekEndDate;
      });

      if (currentWeekKey) {
        setExpandedWeeks(new Set([currentWeekKey]));
      } else {
        // If no current week found, expand the first future week
        const sortedWeeks = Object.keys(groupedMatches).sort((a, b) => {
          return new Date(a).getTime() - new Date(b).getTime();
        });
        const futureWeeks = sortedWeeks.filter(weekStart => new Date(weekStart) > today);
        if (futureWeeks.length > 0) {
          setExpandedWeeks(new Set([futureWeeks[0]]));
        }
      }
    }
  }, [matches]);

  const loadSeasons = async () => {
    try {
      const seasonsData = await sportsApi.getAllSeasons();
      setSeasons(seasonsData);

      // Auto-select current season if available
      const currentSeason = seasonsData.find(s => s.isCurrent);
      if (currentSeason) {
        setSelectedSeasonId(currentSeason.id);
      }
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  };

  const loadClubs = async () => {
    try {
      const clubsData = await sportsApi.getAllClubs();
      setClubs(clubsData);
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  };

  const loadUpcomingMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('loadUpcomingMatches - Start');
      console.log('clubId prop:', clubId);
      console.log('isPartnerUser:', isPartnerUser);
      console.log('user?.preferredClubId:', user?.preferredClubId);

      // Load matches for both club and partner users
      let upcomingMatchesData: Match[] = [];
      let pastMatchesData: Match[] = [];

      // Load upcoming matches
      if (clubId) {
        console.log('Loading matches filtered by clubId:', clubId);
        upcomingMatchesData = await sportsApi.getUpcomingMatchesByClub(clubId, daysAhead);
        console.log('Loaded', upcomingMatchesData.length, 'upcoming matches for club');
      } else {
        console.log('Loading ALL matches (no clubId filter)');
        upcomingMatchesData = await sportsApi.getUpcomingMatches(daysAhead);
        console.log('Loaded', upcomingMatchesData.length, 'upcoming matches (all)');
      }

      // Load all past matches (no date limit)
      if (clubId) {
        pastMatchesData = await sportsApi.getPastMatchesByClub(clubId);
      } else {
        pastMatchesData = await sportsApi.getPastMatches();
      }

      // Filter by selected season if set
      if (selectedSeasonId !== null) {
        upcomingMatchesData = upcomingMatchesData.filter(match => {
          return match.matchday?.season?.id === selectedSeasonId;
        });
        pastMatchesData = pastMatchesData.filter(match => {
          return match.matchday?.season?.id === selectedSeasonId;
        });
      }

      // Combine matches (own matches are part of the unified Match source now)
      const allMatches = [
        ...upcomingMatchesData,
        ...pastMatchesData
      ];

      setTotalMatchCount(allMatches.length);

      // Sort by scheduled date (or date as fallback) and limit results
      const sortedMatches = allMatches
        .sort((a, b) => {
          const dateA = new Date('scheduledDate' in a ? a.scheduledDate : (a as any).date).getTime();
          const dateB = new Date('scheduledDate' in b ? b.scheduledDate : (b as any).date).getTime();
          return dateA - dateB;
        })
        .slice(0, maxMatches);

      setMatches(sortedMatches);
    } catch (err) {
      console.error('Error loading upcoming matches:', err);
      setError(t('common:matches.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlanMatch = (match: Match) => {
    navigate(`/matchday-planning?matchId=${match.id}`);
  };

  // Delete an own match (Testspiel/Training) including any associated planning.
  const handleDeleteMatch = async (match: Match) => {
    const isOwn = match.matchType === MatchType.Friendly || match.matchType === MatchType.Training;
    if (!isOwn) return;
    if (!window.confirm('Spiel wirklich löschen? Eine zugehörige Spieltagsplanung wird ebenfalls entfernt.')) return;
    try {
      // Cross-DB cleanup: remove the planning first if one exists.
      try {
        const planning = await matchdayPlanningApi.getPlanningByEvent(undefined, match.id);
        if (planning?.id) await matchdayPlanningApi.deletePlanning(planning.id);
      } catch (planningErr) {
        console.warn('Could not delete planning for match', match.id, planningErr);
      }
      await sportsApi.deleteMatch(match.id);
      await loadUpcomingMatches();
    } catch (err) {
      console.error('Error deleting match:', err);
      alert('Fehler beim Löschen des Spiels: ' + (err instanceof Error ? err.message : 'Unbekannter Fehler'));
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Helper function to sort matches within a week by date
  const sortMatchesInWeek = (weekMatches: (Match | LocalMatch)[]): (Match | LocalMatch)[] => {
    return [...weekMatches].sort((a, b) => {
      const dateA = new Date('scheduledDate' in a ? a.scheduledDate : (a as any).date).getTime();
      const dateB = new Date('scheduledDate' in b ? b.scheduledDate : (b as any).date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
  };

  // Helper function to determine if a match is a home match
  const isHomeMatch = (match: Match | LocalMatch): boolean | null => {
    if (!user?.preferredClubId) return null;

    // For regular matches
    if ('matchday' in match && match.matchday) {
      // Check if user's club is playing
      if (match.homeClubId === user.preferredClubId) {
        return true; // Home match
      } else if (match.awayClubId === user.preferredClubId) {
        return false; // Away match
      }
    }

    // For local matches - we don't have club info, so return null
    return null;
  };

  // Helper function to get match color based on home/away status
  const getMatchColor = (match: Match | LocalMatch): string | undefined => {
    console.log('getMatchColor called', {
      user: {
        preferredClubId: user?.preferredClubId,
        homeMatchColor: user?.homeMatchColor,
        awayMatchColor: user?.awayMatchColor
      },
      match: {
        id: match.id,
        homeClubId: 'matchday' in match ? match.homeClubId : 'N/A',
        awayClubId: 'matchday' in match ? match.awayClubId : 'N/A'
      }
    });

    if (!user?.preferredClubId || !user?.homeMatchColor || !user?.awayMatchColor) {
      console.log('Missing user data');
      return undefined;
    }

    const isHome = isHomeMatch(match);
    console.log('isHomeMatch result:', isHome);

    // Only apply color if the user's club is actually playing
    if (isHome === null) {
      console.log('User club not playing in this match');
      return undefined;
    }

    const color = isHome ? user.homeMatchColor : user.awayMatchColor;
    console.log('Applying color:', color, 'for', isHome ? 'HOME' : 'AWAY', 'match');
    return color;
  };

  const groupMatchesByWeek = (matches: (Match | LocalMatch)[]) => {
    const groups: { [key: string]: (Match | LocalMatch)[] } = {};

    matches.forEach(match => {
      // Use scheduledDate if available, otherwise fall back to date
      const matchDate = 'scheduledDate' in match ? match.scheduledDate : (match as any).date;
      const date = new Date(matchDate);
      const weekStart = new Date(date);

      // Calculate Monday as week start (getDay(): Sunday=0, Monday=1, ..., Saturday=6)
      const dayOfWeek = date.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday: subtract 6, others: subtract (dayOfWeek - 1)
      weekStart.setDate(date.getDate() - daysToSubtract);

      const weekKey = weekStart.toISOString().split('T')[0];

      if (!groups[weekKey]) {
        groups[weekKey] = [];
      }
      groups[weekKey].push(match);
    });

    return groups;
  };

  const getWeekNumber = (date: Date): number => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const formatWeekLabel = (weekStart: string) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return `${start.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - ${end.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  };

  const getCompetitionStats = (weekMatches: (Match | LocalMatch)[]) => {
    const stats: { [key: string]: number } = {};

    weekMatches.forEach(match => {
      let competitionName = t('common:matches.other');

      if ('matchday' in match && match.matchday?.competition) {
        competitionName = match.matchday.competition.shortName || match.matchday.competition.name;
      } else if ('localMatchday' in match && match.localMatchday) {
        competitionName = t('common:matches.local');
      }

      stats[competitionName] = (stats[competitionName] || 0) + 1;
    });

    return stats;
  };

  const getHomeAwayStats = (weekMatches: (Match | LocalMatch)[]) => {
    let homeCount = 0;
    let awayCount = 0;

    weekMatches.forEach(match => {
      const matchType = isHomeMatch(match);
      if (matchType === true) {
        homeCount++;
      } else if (matchType === false) {
        awayCount++;
      }
    });

    return { homeCount, awayCount };
  };

  // Get competition stats with home/away information
  const getCompetitionStatsWithHomeAway = (weekMatches: (Match | LocalMatch)[]) => {
    const stats: { [key: string]: { home: number; away: number; total: number } } = {};

    weekMatches.forEach(match => {
      let competitionName = t('common:matches.other');

      if ('matchday' in match && match.matchday?.competition) {
        competitionName = match.matchday.competition.shortName || match.matchday.competition.name;
      } else if ('localMatchday' in match && match.localMatchday) {
        competitionName = t('common:matches.local');
      }

      if (!stats[competitionName]) {
        stats[competitionName] = { home: 0, away: 0, total: 0 };
      }

      const matchType = isHomeMatch(match);
      if (matchType === true) {
        stats[competitionName].home++;
      } else if (matchType === false) {
        stats[competitionName].away++;
      }
      stats[competitionName].total++;
    });

    return stats;
  };

  // Check if a week is an "English week" (more than one match)
  const isEnglishWeek = (weekMatches: (Match | LocalMatch)[]): boolean => {
    return weekMatches.length > 1;
  };

  const toggleWeek = (weekKey: string) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekKey)) {
        newSet.delete(weekKey);
      } else {
        newSet.add(weekKey);
      }
      return newSet;
    });
  };

  const toggleAllWeeks = () => {
    const groupedMatches = groupMatchesByWeek(matches);
    const allWeekKeys = Object.keys(groupedMatches);

    // If all weeks are expanded, collapse all; otherwise expand all
    const allExpanded = allWeekKeys.every(weekKey => expandedWeeks.has(weekKey));

    if (allExpanded) {
      setExpandedWeeks(new Set());
    } else {
      setExpandedWeeks(new Set(allWeekKeys));
    }
  };

  // Easter egg: click counter on ⚽
  const handleBallClick = useCallback(async () => {
    const newCount = easterEggClicks + 1;
    setEasterEggClicks(newCount);
    if (newCount >= 7) {
      setEasterEggClicks(0);
      setShowDriverPoints(true);
      setDriverPointsLoading(true);
      try {
        const [allPlannings, allMatches] = await Promise.all([
          matchdayPlanningApi.getAllPlannings(),
          sportsApi.getAllMatches() as Promise<Match[]>,
        ]);
        const preferredClubId = user?.preferredClubId;
        if (!preferredClubId) {
          setDriverPointsData([]);
          setDriverPointsLoading(false);
          return;
        }
        const matchMap = new Map<number, Match>();
        for (const m of allMatches) {
          matchMap.set(m.id, m);
        }
        const pointsMap = new Map<string, { points: number; matches: number }>();
        for (const planning of allPlannings) {
          if (!planning.matchId) continue;
          const match = matchMap.get(planning.matchId);
          if (!match || match.status !== MatchStatus.Finished) continue;
          if (match.homeScore == null || match.awayScore == null) continue;
          const isHome = match.homeClubId === preferredClubId;
          const isAway = match.awayClubId === preferredClubId;
          if (!isHome && !isAway) continue;
          let matchPoints = 0;
          if (match.homeScore === match.awayScore) {
            matchPoints = 1;
          } else if ((isHome && match.homeScore > match.awayScore) || (isAway && match.awayScore > match.homeScore)) {
            matchPoints = 3;
          }
          const drivers: string[] = [];
          if (planning.vehicleAssignments) {
            for (const va of planning.vehicleAssignments) {
              const driverName = va.traveler?.fullName || va.partnerDriver?.fullName;
              if (driverName) drivers.push(driverName);
            }
          }
          if (drivers.length === 0) continue;
          const pointsPerDriver = matchPoints / drivers.length;
          for (const driverName of drivers) {
            const existing = pointsMap.get(driverName) || { points: 0, matches: 0 };
            existing.points += pointsPerDriver;
            existing.matches += 1;
            pointsMap.set(driverName, existing);
          }
        }
        const result = Array.from(pointsMap.entries())
          .map(([name, data]) => ({ name, points: data.points, matches: data.matches }))
          .sort((a, b) => b.points - a.points);
        setDriverPointsData(result);
      } catch (err) {
        console.error('Easter egg data fetch error:', err);
        setDriverPointsData([]);
      } finally {
        setDriverPointsLoading(false);
      }
    }
  }, [easterEggClicks, user?.preferredClubId]);

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={spinnerStyle}></div>
        <p>{t('common:loading')}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>{error}</p>
        <button onClick={loadUpcomingMatches} style={retryButtonStyle}>
          {t('common:refresh')}
        </button>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <div style={emptyIconStyle}>—</div>
        <h3>{t('common:matches.noMatches')}</h3>
        <p>{t('common:noData')}</p>
      </div>
    );
  }

  const groupedMatches = groupMatchesByWeek(matches);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

  // Separate past and future/current weeks
  const pastWeeks: [string, (Match | LocalMatch)[]][] = [];
  const currentAndFutureWeeks: [string, (Match | LocalMatch)[]][] = [];

  Object.entries(groupedMatches).forEach(([weekStart, weekMatches]) => {
    const weekStartDate = new Date(weekStart);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekStartDate.getDate() + 6);
    weekEndDate.setHours(23, 59, 59, 999);

    if (weekEndDate < today) {
      pastWeeks.push([weekStart, weekMatches]);
    } else {
      currentAndFutureWeeks.push([weekStart, weekMatches]);
    }
  });

  // Sort weeks by date based on sortOrder
  pastWeeks.sort(([weekA], [weekB]) => {
    const dateA = new Date(weekA).getTime();
    const dateB = new Date(weekB).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  currentAndFutureWeeks.sort(([weekA], [weekB]) => {
    const dateA = new Date(weekA).getTime();
    const dateB = new Date(weekB).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Count total past matches
  const totalPastMatches = pastWeeks.reduce((sum, [, weekMatches]) => sum + weekMatches.length, 0);
  const totalFutureMatches = currentAndFutureWeeks.reduce((sum, [, wm]) => sum + wm.length, 0);

  return (
    <div className="page-container" style={containerStyle}>
      {showDriverPoints && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowDriverPoints(false)}>
          <div style={{
            backgroundColor: 'white', borderRadius: 12, padding: 24,
            minWidth: 340, maxWidth: 500, maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Fahrer-Punktetabelle</h3>
              <button onClick={() => setShowDriverPoints(false)} style={{
                background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666'
              }}>✕</button>
            </div>
            {driverPointsLoading ? (
              <p style={{ textAlign: 'center', color: '#888' }}>Lade Daten...</p>
            ) : driverPointsData.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888' }}>Keine Daten verfügbar</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: 13 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: 13 }}>Fahrer</th>
                    <th style={{ textAlign: 'center', padding: '8px 4px', fontSize: 13 }}>Spiele</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: 13 }}>Punkte</th>
                  </tr>
                </thead>
                <tbody>
                  {driverPointsData.map((d, i) => (
                    <tr key={d.name} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 4px', fontSize: 14, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#333', fontWeight: i < 3 ? 'bold' : 'normal' }}>
                        {`${i + 1}.`}
                      </td>
                      <td style={{ padding: '6px 4px', fontSize: 14 }}>{d.name}</td>
                      <td style={{ padding: '6px 4px', fontSize: 14, textAlign: 'center' }}>{d.matches}</td>
                      <td style={{ padding: '6px 4px', fontSize: 14, textAlign: 'right', fontWeight: 'bold' }}>
                        {Number.isInteger(d.points) ? d.points : d.points.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p style={{ marginTop: 12, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
              S = 3 Pkt · U = 1 Pkt · N = 0 Pkt · bei 2 Fahrern geteilt
            </p>
          </div>
        </div>
      )}
      {showHeader && (
        <div className="planning-header">
          {/* Row 1: Buttons - left group + right group */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {/* Left: Aktualisieren → Sort → Vergangene */}
            <button onClick={loadUpcomingMatches} className="btn btn-primary"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            >
              {t('common:refresh')}
            </button>

            {!isPartnerUser && (
              <button onClick={() => setShowCreateForm(true)} className="btn btn-primary"
                style={{ padding: '4px 10px', fontSize: '0.8rem', backgroundColor: '#27ae60', borderColor: '#27ae60' }}
              >
                + Eigenes Spiel
              </button>
            )}

            <button onClick={toggleSortOrder} className="btn btn-secondary"
              style={{
                padding: '4px 10px', fontSize: '0.8rem',
                backgroundColor: '#9b59b6', color: 'white', borderColor: '#9b59b6'
              }}
            >
              {sortOrder === 'asc' ? '▲' : '▼'} {sortOrder === 'asc' ? t('common:matches.ascending') : t('common:matches.descending')}
            </button>

            {pastWeeks.length > 0 && (
              <button
                onClick={() => setShowPastMatches(!showPastMatches)}
                className="btn btn-secondary"
                style={{
                  padding: '4px 10px', fontSize: '0.8rem',
                  backgroundColor: showPastMatches ? '#495057' : '#6c757d',
                  color: 'white', borderColor: showPastMatches ? '#495057' : '#6c757d'
                }}
                title={`${totalPastMatches} vergangene Spiele ${showPastMatches ? 'ausblenden' : 'anzeigen'}`}
              >
                {showPastMatches ? '▼' : '▶'} Vergangene ({totalPastMatches})
              </button>
            )}

            {onBackToLanding && (
              <button onClick={onBackToLanding} className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              >
                {t('common:backToHome')}
              </button>
            )}

            {/* Spacer pushes right group to the right */}
            <div style={{ flex: 1 }} />

            {/* Right: Details → View Toggle */}
            {viewMode === 'cards' ? (
              <button onClick={toggleAllWeeks} className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              >
                {(() => {
                  const groupedMatches = groupMatchesByWeek(matches);
                  const allWeekKeys = Object.keys(groupedMatches);
                  const allExpanded = allWeekKeys.every(weekKey => expandedWeeks.has(weekKey));
                  return allExpanded ? t('common:close') : t('common:details');
                })()}
              </button>
            ) : (
              <button disabled className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: '0.8rem', opacity: 0.6, cursor: 'default' }}
              >
                {t('common:details')}
              </button>
            )}

            <div style={viewToggleContainerStyle}>
              <button
                onClick={() => setViewMode('plantafel')}
                style={viewMode === 'plantafel' ? viewToggleActiveStyle : viewToggleButtonStyle}
                title="Liste (Plantafel)"
              >
                ☰
              </button>
              <button
                onClick={() => setViewMode('cards')}
                style={viewMode === 'cards' ? viewToggleActiveStyle : viewToggleButtonStyle}
                title="Kacheln"
              >
                ▦
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                style={viewMode === 'calendar' ? viewToggleActiveStyle : viewToggleButtonStyle}
                title="Kalender"
              >
                ▨
              </button>
            </div>
          </div>

          {/* Row 2: Filter left, Result right */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', marginTop: '8px', gap: '8px', whiteSpace: 'nowrap' }}>
            <span className="planning-date" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <span onClick={handleBallClick} style={{ cursor: 'default', userSelect: 'none' }}><CircleDot size={16} /></span>
              {t('common:matches.filterBySeason')}:
              <select
                id="season-filter"
                value={selectedSeasonId || ''}
                onChange={(e) => setSelectedSeasonId(e.target.value ? parseInt(e.target.value) : null)}
                style={{
                  padding: '2px 8px', fontSize: '0.85rem',
                  border: '1px solid #dee2e6', borderRadius: '4px',
                  backgroundColor: 'white', cursor: 'pointer', outline: 'none',
                  minWidth: '140px'
                }}
              >
                <option value="">{t('common:matches.allSeasons')}</option>
                {seasons.map(season => (
                  <option key={season.id} value={season.id}>
                    {season.name} {season.isCurrent ? `(${t('common:active')})` : ''}
                  </option>
                ))}
              </select>
            </span>

            <span className="planning-date" style={{ fontSize: '0.85rem' }}>
              {(() => {
                const selectedSeason = seasons.find(s => s.id === selectedSeasonId);
                const seasonText = selectedSeason ? selectedSeason.name : t('common:matches.allSeasons');
                const clubText = filterClub ? ` - ${filterClub.name}` : '';
                const visibleCount = showPastMatches ? (totalPastMatches + totalFutureMatches) : totalFutureMatches;
                return `${visibleCount} / ${totalMatchCount} Spiele - ${seasonText}${clubText}`;
              })()}
            </span>
          </div>
        </div>
      )}

      {/* Cards View */}
      {viewMode === 'cards' && (
      <div style={contentStyle}>
        {/* Panel 1: Vergangene Spiele */}
        {pastWeeks.length > 0 && showPastMatches && (
          <div style={panelStyle}>
            <div style={panelHeaderStyle}>
              <span style={panelTitleStyle}>Vergangene Spiele</span>
              <span className="planning-date" style={{ fontSize: '0.8rem' }}>
                {totalPastMatches} Spiele
              </span>
            </div>
            <div style={panelContentStyle}>
              {pastWeeks.map(([weekStart, weekMatches]) => {
                const weekStartDate = new Date(weekStart);
                const weekNumber = getWeekNumber(weekStartDate);
                const isExpanded = expandedWeeks.has(weekStart);
                const competitionStatsWithHomeAway = getCompetitionStatsWithHomeAway(weekMatches);
                const englishWeek = isEnglishWeek(weekMatches);

                return (
                  <div key={weekStart} style={nestedWeekSectionStyle}>
                    <div
                      style={{
                        ...weekHeaderStyle,
                        ...(englishWeek ? {
                          backgroundColor: '#fff3cd',
                          borderLeft: '4px solid #ffc107'
                        } : {})
                      }}
                      onClick={() => toggleWeek(weekStart)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = englishWeek ? '#ffe69c' : '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = englishWeek ? '#fff3cd' : '#f8f9fa';
                      }}
                    >
                      <div style={weekHeaderLeftStyle}>
                        <span style={expandIconStyle}>{isExpanded ? '▼' : '▶'}</span>
                        <span style={weekNumberStyle}>KW {weekNumber}</span>
                        <span style={weekDateRangeStyle}>{formatWeekLabel(weekStart)}</span>
                      </div>
                      <div style={weekHeaderRightStyle}>
                        {englishWeek && (<span style={englishWeekBadgeStyle}>Englische Woche</span>)}
                        {Object.entries(competitionStatsWithHomeAway).map(([competition, stats]) => {
                          let backgroundColor = '#6c757d';
                          if (stats.home > 0 && stats.away === 0) backgroundColor = user?.homeMatchColor || '#4CAF50';
                          else if (stats.away > 0 && stats.home === 0) backgroundColor = user?.awayMatchColor || '#FF9800';
                          else if (stats.home > 0 && stats.away > 0) backgroundColor = stats.home >= stats.away ? (user?.homeMatchColor || '#4CAF50') : (user?.awayMatchColor || '#FF9800');
                          return (<span key={competition} style={{ ...competitionBadgeStyle, backgroundColor, color: 'white' }}>{competition}: {stats.total}</span>);
                        })}
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={weekContentStyle}>
                        <div style={cardsGridStyle}>
                          {sortMatchesInWeek(weekMatches).map(match => (
                            <UpcomingMatchCard key={match.id} match={match as unknown as Match} onPlanMatch={handlePlanMatch} onDeleteMatch={handleDeleteMatch} backgroundColor={getMatchColor(match)} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notice when no future matches exist */}
        {currentAndFutureWeeks.length === 0 && pastWeeks.length > 0 && (
          <div style={noFutureMatchesNoticeStyle}>
            <span style={noFutureMatchesIconStyle}>i</span>
            <span>{t('common:matches.noFutureMatches')}</span>
          </div>
        )}

        {/* Panel 2: Kommende Spiele */}
        {currentAndFutureWeeks.length > 0 && (
          <div style={panelStyle}>
            <div style={panelHeaderStyle}>
              <span style={panelTitleStyle}>Kommende Spiele</span>
              <span className="planning-date" style={{ fontSize: '0.8rem' }}>
                {currentAndFutureWeeks.reduce((sum, [, wm]) => sum + wm.length, 0)} Spiele
              </span>
            </div>
            <div style={panelContentStyle}>
              {currentAndFutureWeeks.map(([weekStart, weekMatches]) => {
                const weekStartDate = new Date(weekStart);
                const weekNumber = getWeekNumber(weekStartDate);
                const isExpanded = expandedWeeks.has(weekStart);
                const competitionStatsWithHomeAway = getCompetitionStatsWithHomeAway(weekMatches);
                const englishWeek = isEnglishWeek(weekMatches);

                return (
                  <div key={weekStart} style={nestedWeekSectionStyle}>
                    <div
                      style={{
                        ...weekHeaderStyle,
                        ...(englishWeek ? {
                          backgroundColor: '#fff3cd',
                          borderLeft: '4px solid #ffc107'
                        } : {})
                      }}
                      onClick={() => toggleWeek(weekStart)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = englishWeek ? '#ffe69c' : '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = englishWeek ? '#fff3cd' : '#f8f9fa';
                      }}
                    >
                      <div style={weekHeaderLeftStyle}>
                        <span style={expandIconStyle}>{isExpanded ? '▼' : '▶'}</span>
                        <span style={weekNumberStyle}>KW {weekNumber}</span>
                        <span style={weekDateRangeStyle}>{formatWeekLabel(weekStart)}</span>
                      </div>
                      <div style={weekHeaderRightStyle}>
                        {englishWeek && (<span style={englishWeekBadgeStyle}>Englische Woche</span>)}
                        {Object.entries(competitionStatsWithHomeAway).map(([competition, stats]) => {
                          let backgroundColor = '#6c757d';
                          if (stats.home > 0 && stats.away === 0) backgroundColor = user?.homeMatchColor || '#4CAF50';
                          else if (stats.away > 0 && stats.home === 0) backgroundColor = user?.awayMatchColor || '#FF9800';
                          else if (stats.home > 0 && stats.away > 0) backgroundColor = stats.home >= stats.away ? (user?.homeMatchColor || '#4CAF50') : (user?.awayMatchColor || '#FF9800');
                          return (<span key={competition} style={{ ...competitionBadgeStyle, backgroundColor, color: 'white' }}>{competition}: {stats.total}</span>);
                        })}
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={weekContentStyle}>
                        <div style={cardsGridStyle}>
                          {sortMatchesInWeek(weekMatches).map(match => (
                            <UpcomingMatchCard key={match.id} match={match as unknown as Match} onPlanMatch={handlePlanMatch} onDeleteMatch={handleDeleteMatch} backgroundColor={getMatchColor(match)} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Plantafel View */}
      {viewMode === 'plantafel' && (
        <div className="planning-timeline">
          <Plantafel
            matches={matches}
            onPlanMatch={handlePlanMatch}
            onDeleteMatch={handleDeleteMatch}
            userClubId={user?.preferredClubId}
            homeMatchColor={user?.homeMatchColor}
            awayMatchColor={user?.awayMatchColor}
            sortOrder={sortOrder}
            showPastMatches={showPastMatches}
          />
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div style={calendarContainerStyle}>
          {(() => {
            // Group matches by month - filter past matches unless showPastMatches is on
            const now = new Date();
            const monthGroups: Record<string, (Match | LocalMatch)[]> = {};
            const sortedAll = [...matches].sort((a, b) => {
              const dateA = new Date('scheduledDate' in a ? a.scheduledDate : (a as any).date).getTime();
              const dateB = new Date('scheduledDate' in b ? b.scheduledDate : (b as any).date).getTime();
              return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            });
            // Filter: when showPastMatches is off, only show current month and future
            const filteredForCalendar = showPastMatches ? sortedAll : sortedAll.filter(m => {
              const d = new Date('scheduledDate' in m ? m.scheduledDate : (m as any).date);
              // Keep if match is in current month or future
              return d.getFullYear() > now.getFullYear() ||
                (d.getFullYear() === now.getFullYear() && d.getMonth() >= now.getMonth());
            });
            for (const m of filteredForCalendar) {
              const d = new Date('scheduledDate' in m ? m.scheduledDate : (m as any).date);
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              if (!monthGroups[key]) monthGroups[key] = [];
              monthGroups[key].push(m);
            }

            return Object.entries(monthGroups).map(([monthKey, monthMatches]) => {
              const [year, month] = monthKey.split('-').map(Number);
              const firstDay = new Date(year, month - 1, 1);
              const lastDay = new Date(year, month, 0);
              const startWeekday = (firstDay.getDay() + 6) % 7; // Monday=0
              const daysInMonth = lastDay.getDate();
              const monthName = firstDay.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });

              // Build day-to-matches map
              const dayMatchMap: Record<number, (Match | LocalMatch)[]> = {};
              for (const m of monthMatches) {
                const d = new Date('scheduledDate' in m ? m.scheduledDate : (m as any).date);
                const day = d.getDate();
                if (!dayMatchMap[day]) dayMatchMap[day] = [];
                dayMatchMap[day].push(m);
              }

              const cells: React.ReactNode[] = [];
              // Empty cells before first day
              for (let i = 0; i < startWeekday; i++) {
                cells.push(<div key={`empty-${i}`} style={calendarCellEmptyStyle} />);
              }
              for (let day = 1; day <= daysInMonth; day++) {
                const dayMatches = dayMatchMap[day] || [];
                const isToday = new Date().getDate() === day && new Date().getMonth() === month - 1 && new Date().getFullYear() === year;
                cells.push(
                  <div key={day} style={{ ...calendarCellStyle, ...(isToday ? calendarTodayStyle : {}) }}>
                    <div style={calendarDayNumberStyle}>{day}</div>
                    {dayMatches.map((m, idx) => {
                      const mm = m as Match;
                      const homeLabel = mm.homeClub?.shortName || mm.homeClub?.name || mm.homeTeamName || '?';
                      const awayLabel = mm.awayClub?.shortName || mm.awayClub?.name || mm.awayTeamName || '?';
                      const matchName = `${homeLabel} - ${awayLabel}`;
                      const matchColor = getMatchColor(m);
                      const matchDate = new Date('scheduledDate' in m ? m.scheduledDate : (m as any).date);
                      const isPast = matchDate < now;
                      return (
                        <div key={idx} style={{ ...calendarMatchStyle, backgroundColor: matchColor || '#e3f2fd', opacity: isPast ? 0.5 : 1 }}>
                          <span style={{ fontSize: 11, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {matchName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div key={monthKey} style={{ marginBottom: 24 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#2c3e50' }}>{monthName}</h3>
                  <div style={calendarHeaderRowStyle}>
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                      <div key={d} style={calendarHeaderCellStyle}>{d}</div>
                    ))}
                  </div>
                  <div style={calendarGridStyle}>
                    {cells}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {showCreateForm && (
        <CreateOwnMatchModal
          seasons={seasons}
          clubs={clubs}
          defaultSeasonId={selectedSeasonId}
          preferredClubId={user?.preferredClubId}
          onClose={() => setShowCreateForm(false)}
          onCreated={() => { setShowCreateForm(false); loadUpcomingMatches(); }}
        />
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  width: '100%',
  padding: '20px',
  background: '#f5f5f5',
  minHeight: '100vh',
  boxSizing: 'border-box'
};

const contentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
};

const panelStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  width: '100%',
};

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 20px',
  borderBottom: '1px solid #e9ecef',
  backgroundColor: '#fafafa',
};

const panelTitleStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#333',
};

const panelContentStyle: React.CSSProperties = {
  padding: '16px',
};

const weekSectionStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  marginBottom: '12px'
};

const nestedWeekSectionStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
  marginBottom: '8px'
};

const weekHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  borderBottom: '1px solid #e9ecef',
  userSelect: 'none'
};

const pastWeekHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  backgroundColor: '#f1f3f5',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  borderBottom: '1px solid #dee2e6',
  userSelect: 'none'
};

const pastWeekTitleStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#6c757d',
  marginLeft: '8px'
};

const pastMatchesBadgeStyle: React.CSSProperties = {
  padding: '4px 12px',
  backgroundColor: '#6c757d',
  color: 'white',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
};

const pastWeeksContainerStyle: React.CSSProperties = {
  padding: '16px',
  backgroundColor: '#f8f9fa'
};

const weekHeaderLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1
};

const weekHeaderRightStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap'
};

const expandIconStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#3498db',
  fontWeight: 'bold',
  minWidth: '20px'
};

const weekNumberStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#2c3e50',
  minWidth: '60px'
};

const weekDateRangeStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: '500',
  color: '#34495e'
};

const competitionBadgeStyle: React.CSSProperties = {
  padding: '4px 12px',
  backgroundColor: '#3498db',
  color: 'white',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
};

const totalMatchesStyle: React.CSSProperties = {
  padding: '4px 12px',
  backgroundColor: '#27ae60',
  color: 'white',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
};

const homeAwayBadgeStyle: React.CSSProperties = {
  padding: '4px 12px',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '600',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px'
};

const weekContentStyle: React.CSSProperties = {
  padding: '24px',
  animation: 'slideDown 0.3s ease-out'
};

const weekTitleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: '600',
  color: '#2c3e50',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '2px solid #e9ecef'
};

const cardsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
  gap: '20px'
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#7f8c8d'
};

const spinnerStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  border: '4px solid #e9ecef',
  borderTop: '4px solid #3498db',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '16px'
};

const errorStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#e74c3c'
};

const retryButtonStyle: React.CSSProperties = {
  marginTop: '16px',
  padding: '10px 20px',
  backgroundColor: '#e74c3c',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer'
};

const emptyStateStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#7f8c8d',
  textAlign: 'center'
};

const emptyIconStyle: React.CSSProperties = {
  fontSize: '48px',
  marginBottom: '16px'
};

const emptyTitleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: '600',
  color: '#2c3e50',
  marginBottom: '8px'
};

const emptyTextStyle: React.CSSProperties = {
  fontSize: '16px',
  color: '#7f8c8d'
};

const weekTitleSectionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

const weekIconStyle: React.CSSProperties = {
  fontSize: '20px'
};

const weekBadgeStyle: React.CSSProperties = {
  padding: '4px 12px',
  backgroundColor: '#3498db',
  color: 'white',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
};

const matchesGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
  gap: '20px',
  padding: '20px'
};

const englishWeekBadgeStyle: React.CSSProperties = {
  padding: '4px 12px',
  backgroundColor: '#ffc107',
  color: '#000',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: '700',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  boxShadow: '0 2px 4px rgba(255, 193, 7, 0.3)'
};

const noFutureMatchesNoticeStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  backgroundColor: '#e3f2fd',
  borderRadius: '8px',
  marginBottom: '16px',
  border: '1px solid #90caf9',
  color: '#1565c0',
  fontSize: '15px',
  fontWeight: '500'
};

const noFutureMatchesIconStyle: React.CSSProperties = {
  fontSize: '20px'
};

const viewToggleContainerStyle: React.CSSProperties = {
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #dee2e6'
};

const viewToggleButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#f8f9fa',
  color: '#495057',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s'
};

const viewToggleActiveStyle: React.CSSProperties = {
  ...viewToggleButtonStyle,
  backgroundColor: '#007bff',
  color: 'white'
};

const calendarContainerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
};

const calendarGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '1px',
  backgroundColor: '#dee2e6'
};

const calendarHeaderRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '1px',
  backgroundColor: '#dee2e6',
  marginBottom: '1px'
};

const calendarHeaderCellStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  padding: '6px 4px',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  color: '#6c757d'
};

const calendarCellStyle: React.CSSProperties = {
  backgroundColor: 'white',
  minHeight: 70,
  padding: 4,
  verticalAlign: 'top'
};

const calendarCellEmptyStyle: React.CSSProperties = {
  backgroundColor: '#fafafa',
  minHeight: 70
};

const calendarTodayStyle: React.CSSProperties = {
  backgroundColor: '#e8f4fd'
};

const calendarDayNumberStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#495057',
  marginBottom: 2
};

const calendarMatchStyle: React.CSSProperties = {
  padding: '2px 4px',
  borderRadius: 4,
  marginBottom: 2,
  cursor: 'default'
};

export default UpcomingMatchesDashboard;
